import { create } from 'zustand';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import axiosInstance from 'helper/apis/axiosInstance';
import PeerService from 'lib/peerService';
import platform from 'platform';

const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_RECONNECT_DELAY = 2000; // 2 seconds
const log = (message, ...args) => console.log(`[CallStore] ${message}`, ...args);
const logError = (message, ...args) => console.error(`[CallStore] ${message}`, ...args);

const useCallStore = create((set, get) => ({
	// State
	isOpen: false,
	isInitialized: false,
	userId: null,
	userType: null,
	userName: null,
	avatar: null,
	targetUserId: null,
	targetUserName: null,
	targetUserAvatar: null,
	targetUserType: null,
	peerId: '',
	remotePeerId: '',
	callStatus: 'disconnected', // disconnected, calling, incoming, connected
	stream: null,
	isOnline: false,
	callDuration: 0,
	reconnectAttempts: 0,
	isMuted: false,
	peerService: null,
	callDurationInterval: null,
	heartbeatInterval: null,
	reconnectTimeout: null,
	ringtone: null,
	callInstance: null,
	remoteAudio: null,
	callTimeout: null,
	isConnecting: false, // Flag to prevent multiple connection attempts
	isStartingCall: false, // Flag to prevent multiple call attempts
	isHandlingDisconnection: false,
	isCleaningUp: false,

	setPeerUser: ({ userId, userType, userName, avatar }) => {
		const { isInitialized } = get();
		if (isInitialized) return;
		log('Setting peer user', { userId, userType });
		set({ userId, userType, userName, avatar });
	},

	// Modal actions
	showCallModal: ({ userId, userType, userName, avatar, targetUserId, targetUserName, targetUserType, targetUserAvatar, endTime }) => {
		log('Showing call modal', { targetUserId });
		set({
			isOpen: true,
			callStatus: 'calling', // Set status to calling
			targetUserId,
			targetUserName,
			targetUserType,
			targetUserAvatar,
			userId,
			userType,
			userName,
			avatar,
			endTime
		});
	},

	closeModal: () => {
		log('Closing call modal and ending any active call.');
		get().endCall(); // End the call, but don't destroy the peer connection
		set({ isOpen: false }); // Hide the modal
	},

	rejoinCall: () => {
		log('Rejoining call.');
		set({ callStatus: 'calling' });
		get().startCall();
	},

	// Actions
	initializeCall: async () => {
		const { userId, isConnecting, peerService } = get();

		if (isConnecting || peerService || !userId) {
			log('InitializeCall skipped', { isConnecting, peerService: !!peerService, userId });
			return;
		}

		log(`Initializing PeerJS for userId: ${userId}`);
		set({ isConnecting: true, reconnectAttempts: 0 });

		const peerId = `${userId}-${Math.random().toString(36).substring(2, 9)}`;
		const newPeerService = new PeerService({
			onOpen: (id) => {
				log(`PeerJS connection opened. Peer ID: ${id}`);
				set({ peerId: id, reconnectAttempts: 0, isInitialized: true, isConnecting: false });
				get().registerPeer(id);
			},
			onCall: get().handleIncomingCall,
			onCallClose: () => get().endCall(),
			onCallError: (err, message) => {
				logError(`A peer error occurred: ${err.type}`, err);
				logError(message || 'حدث خطأ غير متوقع في الاتصال.');
				get().endCall();
			},
			onDisconnected: (message) => get().handleDisconnection(message),
			onStream: (remoteStream) => {
				log('Received remote audio stream, call is now connected.');
				const { callTimeout } = get();

				// Clear the no-answer timeout
				if (callTimeout) {
					clearTimeout(callTimeout);
				}

				const audio = new Audio();
				audio.srcObject = remoteStream;
				// Defer playback to be triggered by a user gesture
				// audio.play().catch(err => logError("Failed to play remote audio:", err));

				set({
					remoteAudio: audio,
					callStatus: 'connected',
					callTimeout: null
				});

				get().startCallTimer();
			}
		});

		newPeerService.initialize(peerId);
		set({ peerService: newPeerService });
	},

	playRemoteAudio: () => {
		const { remoteAudio } = get();
		if (remoteAudio) {
			log('Attempting to play remote audio...');
			remoteAudio.play().catch(err => logError("Failed to play remote audio:", err));
		}
	},

	handleDisconnection: (message) => {
		const { reconnectAttempts, reconnectTimeout, callStatus, isHandlingDisconnection, isCleaningUp } = get();

		if (isCleaningUp) {
			log('Disconnection due to cleanup, ignoring reconnect.');
			return;
		}

		if (isHandlingDisconnection) {
			log('Disconnection handling already in progress, ignoring.');
			return;
		}
		set({ isHandlingDisconnection: true });
		setTimeout(() => set({ isHandlingDisconnection: false }), 3000); // Cooldown period

		log(`PeerJS disconnected. Attempting reconnect #${reconnectAttempts + 1}`);
		set({ isInitialized: false, isOnline: false });

		if (callStatus !== 'disconnected') {
			log('Call in progress, showing reconnecting status.');
			toast.info(message || 'انقطع الاتصال, جاري اعادة الاتصال...');
		}

		if (reconnectTimeout) {
			clearTimeout(reconnectTimeout);
		}

		if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
			const delay = INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttempts);
			log(`Scheduling reconnect in ${delay}ms`);
			const timeout = setTimeout(() => {
				try {
					const { peerService, userId } = get();
					if (peerService) {
						log('Reconnecting existing peer instance.');
						peerService.reconnect(userId);
					} else {
						log('Peer instance destroyed, re-initializing.');
						get().initializeCall();
					}
					set(state => ({ reconnectAttempts: state.reconnectAttempts + 1 }));
				}
				catch (err) {
					logError('Failed to Reconnecting peer', err);
					get().handleDisconnection();
				}
			}, delay);

			set({ reconnectTimeout: timeout });
		} else {
			logError('Max reconnection attempts reached.');
			toast.error('فشل الاتصال بالخادم. يرجى تحديث الصفحة والمحاولة مرة أخرى.');
			get().cleanup();
		}
	},

	registerPeer: async (peerId) => {
		const { userId } = get();
		log(`Registering peerId ${peerId} for userId ${userId}`);
		try {
			const { userType, userName, avatar } = get();
			const deviceInformation = platform.description;
			await axiosInstance.post('/peers/register', {
				userId,
				peerId,
				userType,
				name: userName,
				online: true,
				avatar,
				deviceInformation
			});
			log('Peer registered successfully. Starting heartbeat.');
			set({ isOnline: true });

			if (get().heartbeatInterval) {
				clearInterval(get().heartbeatInterval);
			}

			const interval = setInterval(() => {
				const { userId: currentUserId, isInitialized: stillInitialized } = get();
				if (!stillInitialized || !currentUserId) {
					log('Halting heartbeat: Call store not initialized or user ID missing.');
					clearInterval(interval);
					return;
				}
				const deviceInformation = platform.description;
				axiosInstance.post('/peers/heartbeat', { userId: currentUserId, deviceInformation })
					.catch(err => {
						logError('Heartbeat failed', err);
						get().handleDisconnection();
					});
			}, 45000); // 45 seconds

			set({ heartbeatInterval: interval });
		} catch (err) {
			logError('Failed to register peer', err);
			toast.error('فشل في تسجيل الجهاز');
			get().handleDisconnection();
		}
	},

	unregisterPeer: async () => {
		const { userId, peerId, userType, userName, avatar } = get();
		log(`Unregistering peer for userId ${userId}`);
		try {
			const deviceInformation = platform.description;
			await axiosInstance.post('/peers/register', {
				userId,
				peerId,
				userType,
				name: userName,
				online: false,
				avatar,
				deviceInformation
			});
			log('Peer unregistered successfully.');
			set({ isOnline: false });

			// Also send a beacon to the disconnect endpoint
			const data = JSON.stringify({ userId, deviceInformation });
			navigator.sendBeacon('/api/peers/disconnect', data);
		} catch (err) {
			logError('Failed to unregister peer', err);
		}
	},

	playRingtone: () => {
		log('Playing ringtone');
		const audio = new Audio('/sounds/ringtone.mp3');
		audio.loop = true;
		audio.play().catch(err => logError('Ringtone playback failed:', err));
		set({ ringtone: audio });
	},

	stopRingtone: () => {
		const { ringtone } = get();
		if (ringtone) {
			log('Stopping ringtone');
			ringtone.pause();
			ringtone.currentTime = 0;
			set({ ringtone: null });
		}
	},

	handleIncomingCall: async (call) => {
		const callerPeerId = call.peer;
		log(`Incoming call from peerId: ${callerPeerId}`);

		if (get().callStatus !== 'disconnected') {
			logError('Call ignored: Already in a call.', { currentStatus: get().callStatus, callerPeerId });
			call.close(); // Explicitly close the new incoming call
			return;
		}

		try {
			const response = await axiosInstance.get(`/peers/get-by-peer?peerId=${callerPeerId}`);
			const callerData = response.data;
			log('Caller data fetched successfully', callerData);

			set({
				callStatus: 'incoming',
				isOpen: true,
				callInstance: call,
				targetUserId: callerData?.userId,
				targetUserName: callerData?.name,
				targetUserType: callerData?.userType,
				targetUserAvatar: callerData?.avatar
			});
		} catch (err) {
			logError('Failed to get caller data, using metadata fallback.', { error: err, peer: callerPeerId });
			const callerInfo = call.metadata || {};
			set({
				callStatus: 'incoming',
				isOpen: true,
				callInstance: call,
				targetUserId: callerInfo?.targetUserId,
				targetUserName: callerInfo?.targetUserName,
				targetUserType: callerInfo?.targetUserType,
				targetUserAvatar: callerInfo?.targetUserAvatar
			});
		}

		get().playRingtone();
	},

	acceptCall: async () => {
		const { callInstance, peerService } = get();
		if (!callInstance || !peerService) {
			logError('acceptCall failed: no callInstance or peerService found.');
			return;
		}
		log('Accepting call...');
		get().stopRingtone();

		if (window.isSecureContext === false) {
			logError('getUserMedia cannot be used in an insecure context. Please use HTTPS.');
			toast.error('لا يمكن الوصول للميكروفون. يجب استخدام اتصال آمن (HTTPS).');
			get().rejectCall();
			return;
		}

		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			log('Microphone access granted.');
			set({ stream });
			peerService.answerCall(callInstance, stream);
		} catch (err) {
			logError('Failed to get user media', { name: err.name, message: err.message });
			if (err.name === 'NotAllowedError') {
				toast.error('تم رفض إذن الوصول إلى الميكروفون. يرجى تمكين الوصول في إعدادات المتصفح.');
			} else if (err.name === 'NotFoundError') {
				toast.error('لم يتم العثور على ميكروفون. يرجى توصيل ميكروفون والمحاولة مرة أخرى.');
			} else {
				toast.error('لا يمكن الوصول للميكروفون. يرجى التحقق من الأذونات.');
			}
			get().rejectCall();
		}
	},

	rejectCall: () => {
		const { callInstance } = get();
		log('Rejecting call.');
		get().stopRingtone();

		try {
			if (callInstance) {
				callInstance.close();
			}
		} catch (err) {
			logError('Error rejecting call:', err);
		} finally {
			get().endCall({ skipToast: true });
		}
	},

	startCall: async () => {
		const { targetUserId, isInitialized, peerService, isStartingCall } = get();
		if (isStartingCall) {
			log('startCall ignored: another call is already being initiated.');
			return;
		}
		set({ isStartingCall: true });

		log(`Attempting to start call to userId: ${targetUserId}`);

		try {
			if (!isInitialized || !peerService) {
				log('Peer not ready, attempting to initialize now...');
				toast.info('جاري تهيئة خدمة الاتصال, يرجى الانتظار...');
				await get().initializeCall(); // Await initialization

				// Wait for initialization with a timeout
				await new Promise((resolve, reject) => {
					const timeout = 10000; // 10 seconds
					const interval = 500;
					let elapsed = 0;

					const check = setInterval(() => {
						const { isInitialized: isReady } = get();
						if (isReady) {
							clearInterval(check);
							resolve();
						} else {
							elapsed += interval;
							if (elapsed >= timeout) {
								clearInterval(check);
								reject(new Error("Initialization timed out."));
							}
						}
					}, interval);
				});
				log('Initialization complete, proceeding with call.');
			}

			const { peerService: currentPeerService, userId, userType, userName, avatar, targetUserName, targetUserType, targetUserAvatar } = get();

			if (!currentPeerService) {
				throw new Error('PeerJS instance is not valid after initialization.');
			}

			if (userId === targetUserId) {
				throw new Error("Cannot call yourself.");
			}

			log(`Fetching remote peerId for userId: ${targetUserId}`);
			let remotePeerData;
			try {
				const response = await axiosInstance.get(`/peers/get?userId=${targetUserId}`);
				remotePeerData = response.data;
				log('Remote peer data:', remotePeerData);
			} catch (err) {
				logError(`Failed to fetch remote peer data for userId: ${targetUserId}`, err);
				throw new Error('لم يتم العثور على المستخدم المطلوب.');
			}


			if (!remotePeerData.peerId) throw new Error(`${remotePeerData.name || 'المستخدم'} غير متاح للمكالمات`);

			if (window.isSecureContext === false) {
				logError('getUserMedia cannot be used in an insecure context. Please use HTTPS.');
				throw new Error('لا يمكن بدء المكالمة. يجب استخدام اتصال آمن (HTTPS).');
			}

			log('Getting user media (microphone)...');
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			log('Microphone access granted.');

			set({ stream, callStatus: 'calling', remotePeerId: remotePeerData.peerId });
			const metadata = { userId, userType, name: userName, avatar, targetUserId, targetUserName, targetUserType, targetUserAvatar, isCaller: true };

			const call = await currentPeerService.makeCall(remotePeerData.peerId, stream, metadata);
			set({ callInstance: call });


			const callTimeout = setTimeout(() => {
				if (get().callStatus === 'calling') {
					logError('Call timed out (no answer).');
					toast.error('لم يتم الرد على المكالمة');
					get().endCall();
				}
			}, 20000); // 20 seconds
			set({ callTimeout });

		} catch (err) {
			logError('startCall failed', { name: err.name, message: err.message });
			if (err.name === 'NotAllowedError') {
				toast.error('تم رفض إذن الوصول إلى الميكروفون. يرجى تمكين الوصول في إعدادات المتصفح.');
			} else if (err.name === 'NotFoundError') {
				toast.error('لم يتم العثور على ميكروفون. يرجى توصيل ميكروفون والمحاولة مرة أخرى.');
			} else {
				toast.error(err.message || 'فشل في بدء المكالمة');
			}
			get().endCall();
		} finally {
			set({ isStartingCall: false });
		}
	},

	endCall: (options = {}) => {
		const { remoteAudio, callTimeout, stream, callInstance, callStatus } = get();
		log('Ending call...', { currentStatus: callStatus });
		get().stopRingtone();

		if (remoteAudio) remoteAudio.pause();
		if (callTimeout) clearTimeout(callTimeout);
		if (stream) stream.getTracks().forEach(track => track.stop());

		if (callInstance && callInstance.open) {
			try {
				callInstance.close();
			} catch (err) {
				logError('Error closing call instance:', err);
			}
		}

		get().stopCallTimer();

		if (callStatus === 'connected' && !options.skipToast) {
			toast.info('انتهت المكالمة');
		}

		set({
			stream: null,
			callStatus: 'disconnected',
			remotePeerId: '',
			callDuration: 0,
			isMuted: false,
			callInstance: null,
			remoteAudio: null,
			callTimeout: null,
			isStartingCall: false,
		});
	},

	startCallTimer: () => {
		if (get().callDurationInterval) return;
		log('Starting call timer.');
		set({ callDuration: 0 });
		const interval = setInterval(() => {
			set(state => ({ callDuration: state.callDuration + 1 }));
			const { endTime } = get();
			if (endTime) {
				const localEndTime = moment.utc(endTime, "HH:mm").tz(moment.tz.guess());
				const now = moment();
				if (now.isAfter(localEndTime)) {
					log('Scheduled end time reached.');
					toast.error("انتهى وقت الجلسة المحدد");
					get().endCall();
				}
			}
		}, 1000);
		set({ callDurationInterval: interval });
	},

	stopCallTimer: () => {
		const { callDurationInterval } = get();
		if (callDurationInterval) {
			log('Stopping call timer.');
			clearInterval(callDurationInterval);
			set({ callDurationInterval: null });
		}
	},

	toggleMute: () => {
		const { stream, isMuted } = get();
		if (stream) {
			log(`Toggling mute to: ${!isMuted}`);
			stream.getAudioTracks().forEach(track => {
				track.enabled = !track.enabled;
			});
			set({ isMuted: !isMuted });
		}
	},

	cleanup: () => {
		const { remoteAudio, heartbeatInterval, reconnectTimeout, peerService } = get();
		log('Cleaning up all call resources.');
		set({ isCleaningUp: true });
		get().endCall({ skipToast: true });

		if (remoteAudio) remoteAudio.pause();
		if (heartbeatInterval) clearInterval(heartbeatInterval);
		if (reconnectTimeout) clearTimeout(reconnectTimeout);

		if (peerService) {
			log('Destroying PeerJS instance.');
			peerService.destroy();
		}

		set({
			isInitialized: false,
			isConnecting: false,
			peerId: '',
			remotePeerId: '',
			callStatus: 'disconnected',
			stream: null,
			isOnline: false,
			callDuration: 0,
			reconnectAttempts: 0,
			isMuted: false,
			peerService: null,
			callDurationInterval: null,
			heartbeatInterval: null,
			reconnectTimeout: null,
			callInstance: null,
			ringtone: null,
			remoteAudio: null,
			callTimeout: null,
			isCleaningUp: false,
		});
	},

	formatCallDuration: (seconds) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
	}
}));

export default useCallStore;


